import axios from "axios";
import Vue from "vue";
import Vuex from "vuex";

import * as CSVParse from "csv-parse";

import utils from "@/utils.js";
import {timeFormat} from "d3-time-format";

function _maxId(passwords) {
    let ids = passwords.map(rec => parseInt(rec.id, 36));
    if (ids.length == 0) {
        ids = [0];
    }
    return Math.max(...ids);
}

async function _importFromPasspack(contents) {
    let processContents = () =>
        new Promise((resolve, _reject) => {
            // reads the file and parses from passpack's CVS. both reading and parsing is async
            CSVParse(contents, {columns: true}, (_err, records) => {
                resolve(records);
            });
        });
    let records = await processContents();
    let newPasswords = [];
    records.forEach(rec => {
        // translates "Entry Name","URL","User ID","Password","Notes","Shared Notes","Tags","Extra Fields","Email"
        // into site, email, pass, notes
        // and stores

        let notes = rec["Notes"] || "";
        if (rec["Email"] && rec["User ID"] && rec["Email"] != rec["User ID"]) {
            // since we just have one login field, deal with case where we have both email and login
            notes = `User ID: ${rec["User ID"]}\n${notes}`;
        }

        let lockboxRec = {
            site: rec["Entry Name"],
            login: rec["Email"] || rec["User ID"],
            pass: rec["Password"],
            notes: notes,
        };

        newPasswords.push(lockboxRec);
    });
    resolve(newPasswords);
}

function _rehashServerPass(serverHash, pass) {
    return utils.passHash(serverHash, `after ${pass} hashing on server`);
}

function _updateOrChange(records, changed) {
    records = [...records];
    let idx = records.map(rec => rec.id).indexOf(changed.id);

    let prevRec = idx != -1 && records[idx].history ? records[idx] : {};
    let changeRec = {};
    Object.keys(prevRec)
        .concat(Object.keys(changed))
        .forEach(field => {
            if (!["history", "id"].includes(field) && prevRec[field] != changed[field]) {
                if (prevRec[field] && !changed[field]) {
                    changeRec[field] = "deleted";
                } else if (changed[field]) {
                    changeRec[field] = changed[field];
                }
            }
        });
    changeRec = [timeFormat("%Y-%m-%d %H:%M:%S")(new Date()), Object.entries(changeRec).sort()];

    changed.history = changed.history || [];
    changed.history.unshift(changeRec);

    if (idx != -1) {
        records[idx] = changed;
    } else {
        let newId = _maxId(records) + 1;
        changed.id = new Number(newId).toString(36);
        records.push(changed);
    }

    return [records, changed.id];
}

Vue.use(Vuex);

let store = new Vuex.Store({
    state: {
        // we have just a few states: signup, login, passwords, passwords-edit. not worth vue-router
        name: window.localStorage.getItem("known") ? "login" : "splash",
        apiRoot: process.env.NODE_ENV == "development" ? "//localhost:8000/api/" : "/api/",
        user: null,
        pack: null,
        loaded: false,

        loggedIn: false,
        locked: true,
        passwords: [],
        notes: [],
        userSalt: "",
        hashedPass: "",
        recentlyUsed: [],
        backup: null,

        profileFailed: false,
        lastError: null,
    },

    mutations: {
        go(state, newStateName) {
            state.name = newStateName;
        },

        initUser(state, payload) {
            state.user = payload.user;
            state.pack = payload.pack;
            state.hashedPass = payload.hashedPass;
            if (payload.salt) {
                state.userSalt = payload.salt;
            }
            state.loggedIn = true;
            state.loaded = true;
        },

        setLoggedIn(state, loggedIn) {
            state.loggedIn = loggedIn;
        },

        setHashedPass(state, hashedPass) {
            state.hashedPass = hashedPass;
        },

        setBackup(state, backup) {
            state.backup = backup;
        },

        unlock(state, hashedPass) {
            if (!state.loaded) {
                throw new Error("Trying to unlock pack before pack has been received!");
            }
            hashedPass = hashedPass || state.hashedPass;
            let pack = utils.decrypt(hashedPass, state.pack);
            state.hashedPass = hashedPass;
            pack = JSON.parse(pack);
            state.userSalt = pack.userSalt;
            state.passwords = pack.passwords || [];
            state.notes = pack.notes || [];
            state.recentlyUsed = pack.recentlyUsed || [];
            state.backup = pack.backup;
            state.locked = false;
        },

        lock(state) {
            state.passwords = [];
            state.notes = [];
            state.hashedPass = "";
            state.userSalt = "";
            state.locked = true;
            // you locked, sir; TODO - i'm mixing password management with state management, which isn't very clean
            state.name = "login";
        },

        encryptPack(state) {
            state.pack = utils.encrypt(state.hashedPass, {
                userSalt: state.userSalt,
                passwords: state.passwords,
                recentlyUsed: state.recentlyUsed,
                notes: state.notes,
                backup: state.backup,
            });
        },

        updatePasswords(state, passwords) {
            // re-encrypts the pack (call after each update to passwords so the two don't get out of sync)
            state.passwords = [...passwords];

            // update recently used - make sure we don't have any recently used deleted ones
            let ids = passwords.map(pass => pass.id);
            state.recentlyUsed = state.recentlyUsed.filter(id => ids.includes(id)).slice(0, 50);

            state.pack = utils.encrypt(state.hashedPass, {
                userSalt: state.userSalt,
                passwords: state.passwords,
                recentlyUsed: state.recentlyUsed,
                notes: state.notes,
            });
        },

        updateNotes(state, notes) {
            // re-encrypts the pack (call after each update to passwords so the two don't get out of sync)
            state.notes = [...notes];
            state.pack = utils.encrypt(state.hashedPass, {
                userSalt: state.userSalt,
                passwords: state.passwords,
                recentlyUsed: state.recentlyUsed,
                notes: state.notes,
            });
        },

        addRecentPass(state, passID) {
            state.recentlyUsed.unshift(passID);
        },

        setError(state, error) {
            state.lastError = error;
        },

        setProfileFailed(state, failed) {
            state.profileFailed = failed;
        },
    },

    actions: {
        async _fetchProfile({state, commit}) {
            // on init we go to the
            try {
                let response = await axios.get(state.apiRoot, {withCredentials: true});
                let user = response.data.user;
                commit("initUser", {
                    user: {...user, gravatar: `https://www.gravatar.com/avatar/${utils.md5(user.email)}`},
                    pack: response.data.pack,
                });
            } catch (error) {
                commit("setProfileFailed", true);
                if (error.response && error.response.status == 401) {
                    commit("setLoggedIn", false);
                } else {
                    commit("setError", error.response ? error.response.data : error);
                }
            }
        },

        async profileLoaded({state}) {
            let timeout = ms => {
                return new Promise(resolve => setTimeout(resolve, ms));
            };

            while (true) {
                if (state.loaded || state.profileFailed) {
                    return true;
                }
                await timeout(50);
            }
        },

        requestLock({commit}) {
            // not to be confused with the lock mutation, we call this action so that the editors can back up their
            // data first
            commit("lock");
        },

        async updatePassPhrase({state, commit, dispatch}, {prevPass, newPass}) {
            let [prevHash, newHash] = [utils.passHash(prevPass), utils.passHash(newPass)];

            let newPassphraseHash = await dispatch("hashOnServer", {pass: newPass});

            let pack = utils.encrypt(newPassphraseHash, {
                userSalt: state.userSalt,
                passwords: state.passwords,
                notes: state.notes,
                recentlyUsed: state.recentlyUsed,
            });

            await axios.post(`${state.apiRoot}update_passphrase`, {prevHash, newHash, pack}, {withCredentials: true});
        },

        updatePass({state, commit, dispatch}, changed) {
            let [passwords, passId] = _updateOrChange(state.passwords, changed);
            commit("addRecentPass", passId);
            commit("updatePasswords", passwords);
            return dispatch("uploadPack");
        },

        deletePass({state, commit, dispatch}, pass) {
            let passwords = [...state.passwords];
            let idx = passwords.map(cred => cred.id).indexOf(pass.id);
            if (idx == -1) {
                return;
            }

            passwords.splice(idx, 1);
            commit("updatePasswords", passwords);
            return dispatch("uploadPack");
        },

        updateNote({state, commit, dispatch}, changed) {
            let [notes, noteId] = _updateOrChange(state.notes, changed);
            commit("updateNotes", notes);
            return dispatch("uploadPack");
        },

        deleteNote({state, commit, dispatch}, note) {
            let notes = [...state.notes];
            let idx = notes.map(rec => rec.id).indexOf(note.id);
            if (idx == -1) {
                return;
            }

            notes.splice(idx, 1);
            commit("updateNotes", notes);
            return dispatch("uploadPack");
        },

        trackCopy({state, commit, dispatch}, pass) {
            commit("addRecentPass", pass.id);
            commit("updatePasswords", state.passwords);
            return dispatch("uploadPack");
        },

        uploadPack({state}) {
            // XXX - handle error conditions. at least show a message or something
            return axios.post(state.apiRoot, {pack: state.pack}, {withCredentials: true});
        },

        async importPasswords({state, dispatch, commit}, file) {
            let readFile = () =>
                new Promise((resolve, _reject) => {
                    // reads the file and parses from passpack's CVS. both reading and parsing is async
                    const reader = new FileReader();
                    reader.onload = e => {
                        let contents = e.target.result;
                        resolve(contents);
                    };
                    reader.readAsText(file);
                });
            let contents = await readFile();
            let passwords;
            let notes;

            if (file.name.substr(-5) == ".json") {
                let data = JSON.parse(contents);
                passwords = data["passwords"];
                notes = data["notes"];
            } else {
                // the other one we support is passpack for now
                passwords = await _importFromPasspack(contents);
            }

            passwords = passwords || [];
            let maxId = _maxId(state.passwords);
            passwords.forEach(rec => {
                maxId += 1;
                rec.id = new Number(maxId).toString(36);
            });
            passwords = [...state.passwords, ...passwords];
            commit("updatePasswords", passwords);

            if (notes) {
                maxId = _maxId(state.notes);
                notes.forEach(rec => {
                    maxId += 1;
                    rec.id = new Number(maxId).toString(36);
                });
                notes = [...state.notes, ...notes];
                commit("updateNotes", notes);
            }

            return dispatch("uploadPack");
        },

        async exportJSON({state}) {
            let res = {};
            let fields = ["passwords", "notes"];
            fields.forEach(field => {
                res[field] = state[field].map(rec => {
                    let copy = Object.assign({}, rec);
                    delete copy.id;
                    return copy;
                });
            });

            let json = JSON.stringify(res, null, 4);
            let objectURL = window.URL.createObjectURL(new Blob([json], {type: "data:text/plain"}));

            let element = document.createElement("a");
            element.setAttribute("href", objectURL);
            element.setAttribute("download", "lockbox-passes.json");
            element.style.display = "none";
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
            URL.revokeObjectURL(objectURL);
        },

        async login({state, commit}, {email, pass, newUser}) {
            // we log in with the salted hash of the password. this is so that the server would never receive the
            // actual password, and generating a rainbow table to match hash to the pass would be too expensive
            // considering that the bundle is properly encrypted, the logging in with password isn't strictly
            // necessary. however, the idea that anyone could just get your bundle just by stating the email, seems
            // somewhat exposed, so no harm.
            // login an unpack password are the same, so right after successfully logging in we unlock the pack
            let passHash = utils.passHash(pass);
            let response = await axios.post(`${state.apiRoot}login`, {email, passHash, newUser}, {withCredentials: true});

            window.localStorage.setItem("known", 1);
            commit("initUser", {
                user: response.data.user,
                pack: response.data.pack,
                hashedPass: _rehashServerPass(response.data.hashedPass),
                salt: newUser ? response.data.salt : "",
            });

            if (!response.data.pack) {
                console.log("generating a new pack");
                commit("updatePasswords", []);
            }

            commit("unlock");
        },

        async logOut({state, commit}) {
            await axios.post(`${state.apiRoot}logout`, {}, {withCredentials: true});
            commit("lock");
            commit("setLoggedIn", false);
            window.localStorage.removeItem("pinData");
            window.localStorage.removeItem("known");
        },

        async hashOnServer({state, commit}, {pass}) {
            let browserHashed = utils.passHash(pass);
            let response = await axios.post(`${state.apiRoot}rehash`, {hashed: browserHashed}, {withCredentials: true});
            return _rehashServerPass(response.data);
        },

        async submitPin({state, dispatch, commit}, pin) {
            let pinData = JSON.parse(window.localStorage.getItem("pinData") || "{}");
            let newPin = !pinData.salt;
            let salt = pinData.salt || utils.randomSalt();
            let pinHash = utils.passHash(pin, salt);

            await dispatch("profileLoaded");
            if (state.profileFailed) {
                throw "profile_failed";
            }

            let data = {
                email: state.user.email,
                pinHash: pinHash,
                newPin: newPin,
                userSalt: newPin ? state.userSalt : "",
            };

            let response = await axios.post(`${state.apiRoot}login`, data, {withCredentials: true});
            let saltedPin = response.data.salted_pin;
            if (newPin) {
                let encryptedHashedPass = utils.encrypt(saltedPin, state.hashedPass);
                window.localStorage.setItem(
                    "pinData",
                    JSON.stringify({
                        salt: salt, // salt used to hash pin client-side. this is not much of a secret
                        hashedPass: encryptedHashedPass,
                    })
                );
            } else {
                let pinData = JSON.parse(window.localStorage.getItem("pinData"));
                let hashedPass = utils.decrypt(saltedPin, pinData.hashedPass);
                commit("setHashedPass", hashedPass);
                commit("unlock");
            }
            commit("go", "passwords");
        },
    },
});

export default store;
